
import React from 'react';
import { motion } from 'framer-motion';
import { Progress } from '@/components/ui/progress';
import { useIsMobile } from '@/hooks/use-mobile';
import { CheckCircle } from 'lucide-react';

interface RegistrationProgressProps {
  currentStep: number;
  totalSteps?: number;
}

const RegistrationProgress: React.FC<RegistrationProgressProps> = ({
  currentStep,
  totalSteps = 2, // Default is 2 steps: Form and QR code
}) => {
  const isMobile = useIsMobile();
  const progressPercentage = (currentStep / totalSteps) * 100;
  
  // Labels for each step
  const stepLabels = ['Điền thông tin', 'Xác nhận đăng ký'];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-6 bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg shadow-sm border border-blue-100"
    >
      <div className="flex justify-between items-center mb-3 relative">
        {/* Progress connecting line */}
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-purple-200 to-amber-200 z-0" />
        
        {stepLabels.map((label, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0.6 }}
            animate={{
              opacity: index < currentStep ? 1 : 0.6,
              scale: index === currentStep - 1 ? 1.05 : 1
            }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center z-10"
          >
            <div 
              className={`flex items-center justify-center w-8 h-8 rounded-full mb-1.5 ${
                index < currentStep 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' 
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {index < currentStep - 1 ? (
                <CheckCircle className="w-5 h-5 text-white" />
              ) : (
                <span className="text-sm font-semibold">{index + 1}</span>
              )}
            </div>
            <span className={`text-${isMobile ? 'xs' : 'sm'} font-medium ${
              index < currentStep ? 'text-blue-600' : 'text-gray-500'
            }`}>
              {label}
            </span>
          </motion.div>
        ))}
      </div>
      
      <Progress 
        value={progressPercentage} 
        className="h-2 bg-blue-100" 
      />
      
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-2 flex justify-end"
      >
        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">
          Bước {currentStep}/{totalSteps}
        </span>
      </motion.div>
    </motion.div>
  );
};

export default RegistrationProgress;
